import { useEffect, useRef, useState } from "react";

/**
 * Generates a poster image for a video URL by loading the first frame
 * into a hidden <video>, seeking ~0.5s in, and drawing it to a canvas.
 * Falls back to a black placeholder if the video can't be read (CORS or decoding).
 */
export const VideoThumbnail = ({ src, alt, className }) => {
  const [poster, setPoster] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    const video = document.createElement("video");
    videoRef.current = video;
    video.crossOrigin = "anonymous";
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.src = src;

    const onLoaded = () => {
      // Seek a little in so we don't get a black first frame
      try {
        video.currentTime = Math.min(0.5, (video.duration || 1) * 0.1);
      } catch {
        // some browsers throw if seeking before data is ready
      }
    };

    const onSeeked = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement("canvas");
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) return;
        // Scale down for perf — card is ~300px wide at most
        const scale = Math.min(1, 480 / w);
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const url = canvas.toDataURL("image/jpeg", 0.7);
        if (!cancelled) setPoster(url);
      } catch {
        // Tainted canvas or other error — leave poster null
      }
    };

    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("seeked", onSeeked);

    return () => {
      cancelled = true;
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("seeked", onSeeked);
      video.src = "";
      videoRef.current = null;
    };
  }, [src]);

  if (poster) {
    return (
      <img
        src={poster}
        alt={alt}
        loading="lazy"
        decoding="async"
        className={className}
      />
    );
  }
  return <div className={`${className} bg-black`} aria-hidden="true" />;
};
